/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useEffect, useRef } from 'react';
import { IJobDetailsProps } from './ModifyServices.Model';
import { Box, CircularProgress, Divider } from '@mui/material';
import theme from '../../../../../Shared/Themes/theme';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';
import { DescriptionText, Match, MatchText } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { Loader } from '../../../../Jobs/Components/JobView/JobsView.styles';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { buttonStyles } from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { useTranslation } from 'react-i18next';
import { styles } from './ModifyServices.Style';
import { ServiceCreationType } from '../../../../../Shared/Constants/App';
import { useModifyServicesContext } from './ModifyServicesContext';

// eslint-disable-next-line sonarjs/cognitive-complexity
const JobDetails: React.FC<IJobDetailsProps> = (props) => {
  const {
    selectedJobs,
    servicesData,
    setIsEditService,
    setIsAddService,
    setServiceTaskMapping,
    isAddService,
    isServiceTaskLoading,
  } = props;

  const { openTaskId, setOpenTaskId, selectedServiceId, setSelectedServiceId, isLoading } = useModifyServicesContext();

  const { divider } = buttonStyles;
  const { t } = useTranslation(['taskManagement', 'jobManagement', 'tableHeader']);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const selectedService =
    servicesData?.find((s: any) => s.mdServiceId === selectedServiceId) || servicesData?.[0] || {};

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenTaskId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpenTaskId]);

  const handleEditService = (serviceId: string, serviceIndex: number) => {
    setIsEditService(true);
    setIsAddService(false);
    setSelectedServiceId(serviceId);
    const service = servicesData?.[serviceIndex];

    setServiceTaskMapping({
      type: ServiceCreationType.SCHEDULE_LEVEL,
      woScheduleMasterId: servicesData?.[serviceIndex]?.woScheduleMasterId || '',
      mdServiceId: service?.mdServiceId ?? '',
      mdServiceName: service?.mdServiceName ?? '',
      mdServiceDescription: service?.mdServiceDescription ?? '',
      serviceMeasure: service?.serviceMeasure ?? 0,
      mdServiceUnits: service?.mdServiceUnits ?? '',
      fromDate: selectedJobs?.[0]?.fromDate ?? '',
      endDate: selectedJobs?.[0]?.endDate ?? '',
      customerId: selectedJobs?.[0]?.customerId ?? '',
    });
  };

  const handleAddService = () => {
    setIsAddService(true);
    setIsEditService(false);
    setSelectedServiceId(null);
  };

  const renderTaskChip = (task: any, hasSubTasks: boolean, isOpen: boolean) => (
    <Box
      onClick={() => hasSubTasks && setOpenTaskId(isOpen ? null : task.mdTaskId)}
      sx={{
        ...styles.taskChip,
        cursor: hasSubTasks ? 'pointer' : 'default',
      }}>
      <DescriptionText>{task.mdTaskName}</DescriptionText>
      {hasSubTasks && (
        <img src={isOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon} width={16} height={16} alt="" />
      )}
    </Box>
  );

  const renderServiceChip = (service: any, index: number) => {
    const isSelected = selectedServiceId === service.mdServiceId;
    return (
      <Box
        key={index}
        onClick={() => handleEditService(service.mdServiceId, index)}
        sx={{
          background: isSelected ? theme.palette.secondary.customColor8 : '#F5F5F5',
          padding: '0.5rem 1rem',
          borderRadius: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          border: isSelected ? theme.palette.info.dark : '1px solid transparent',
          cursor: 'pointer',
          '&:hover': {
            background: isSelected ? theme.palette.secondary.customColor8 : '#EEEEEE',
          },
        }}>
        <DescriptionText>{service.mdServiceName}</DescriptionText>
        <img src={Icons.EditBlack} width={16} height={16} alt="edit" style={{ marginLeft: '4px' }} />
      </Box>
    );
  };

  const renderSubTasksDropdown = (subTasks: any[]) => (
    <Box sx={styles.taskDropdown}>
      {subTasks?.map((sub: any, idx: number) => (
        <Box key={idx} sx={styles.dropdownItem}>
          • {sub.mdTaskName}
        </Box>
      ))}
    </Box>
  );

  const renderTaskItem = (task: any, index: number) => {
    const hasSubTasks = task.subTasks?.length > 0;
    const isOpen = openTaskId === task.mdTaskId;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, marginTop: '1rem', maxWidth: '30rem' }} key={index}>
        <Box sx={{ position: 'relative' }} ref={dropdownRef}>
          {renderTaskChip(task, hasSubTasks, isOpen)}
          {isOpen && hasSubTasks && renderSubTasksDropdown(task.subTasks)}
        </Box>
      </Box>
    );
  };

  const renderActionButton = (label: string, onClick: () => void, disabled: boolean, maxWidth: string = '10rem') => (
    <Box
      sx={{
        ...styles.actionButton,
        maxWidth,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={onClick}>
      <Box sx={styles.actionButtonText}>{label}</Box>
    </Box>
  );

  return (
    <Box
      sx={{
        borderRadius: '1rem',
        backgroundColor: theme.palette.common.white,
        boxShadow: BoxShadow.BoxShadow1,
        padding: '1.5rem',
        margin: '1.875rem 0 1.5rem 0',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Match sx={{ width: 'fit-content' }}>
            <MatchText>{t('jobManagement:jobName')}</MatchText>
          </Match>
          <DescriptionText>{selectedJobs?.[0]?.jobName}</DescriptionText>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Match sx={{ width: 'fit-content' }}>
            <MatchText>{t('jobManagement:scheduleName')}</MatchText>
          </Match>
          <DescriptionText>{selectedJobs?.[0]?.scheduleName}</DescriptionText>
        </Box>
        <Divider sx={{ ...divider, margin: '0.5rem 0' }} />

        {isLoading || isServiceTaskLoading ? (
          <Loader>
            <CircularProgress style={{ marginTop: '2rem', color: '#006B6B' }} />
          </Loader>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Match sx={{ width: 'fit-content' }}>
                <MatchText>{t('tableHeader:serviceName')}</MatchText>
              </Match>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, marginTop: '0.5rem' }}>
                {servicesData?.length > 0 && servicesData[0]?.mdServiceName ? (
                  servicesData?.map(renderServiceChip)
                ) : (
                  <DescriptionText>{t('taskManagement:NoServiceFound')}</DescriptionText>
                )}
              </Box>
            </Box>

            {selectedServiceId && (
              <Box sx={{ paddingBottom: '1rem', marginTop: '1rem' }}>
                <Box
                  sx={{
                    display: !(selectedService?.tasks?.length > 0) ? 'flex' : '',
                    gap: !(selectedService?.tasks?.length > 0) ? 2 : 0,
                    alignItems: !(selectedService?.tasks?.length > 0) ? 'center' : '',
                  }}>
                  <Match sx={{ width: 'fit-content' }}>
                    <MatchText>{t('jobManagement:tasks')}</MatchText>
                  </Match>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, marginTop: '1rem' }}>
                    {selectedService?.tasks?.length > 0 ? (
                      selectedService?.tasks?.map(renderTaskItem)
                    ) : (
                      <DescriptionText>{t('taskManagement:NoTasksFound')}</DescriptionText>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 2,
                marginTop: selectedServiceId ? '1rem' : '2rem',
              }}>
              {renderActionButton(t('taskManagement:AddService'), handleAddService, isAddService)}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default JobDetails;
